import React from 'react';
import { Popper } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactCountryFlag from 'react-country-flag';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { IMenuItem } from 'types/menu';
import { IFavouritesTabs } from 'store/reducers/favouritesTabs';
import { removeFavorite } from 'store/actions/favorites';
import { getModuleIcon, generateMenuItemId } from './utils';
import { LANGUAGE_FLAGS, faStar, faStarOutline } from './constants';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { ModuleIconWrapper, ModuleName, DropdownHeader } from './styles';
import { Paper } from '@material-ui/core';

interface Props {
  anchorEl: HTMLElement | null;
  favorites: IFavouritesTabs;
  onModuleClick: (module: IMenuItem) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export const FavoritesDropdown: React.FC<Props> = ({
  anchorEl,
  favorites,
  onModuleClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  const dispatch = useDispatch();

  // Get all favorites sorted by most recent first
  const sortedFavorites = Object.values(favorites.items).sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));

  const handleRemoveFavorite = (e: React.MouseEvent, moduleId: string) => {
    e.stopPropagation();
    dispatch(removeFavorite(moduleId));
  };

  return (
    <Popper
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement="right-start"
      modifiers={{
        offset: {
          enabled: true,
          offset: '8, 0',
        },
        preventOverflow: {
          enabled: true,
          boundariesElement: 'viewport',
        },
        flip: {
          enabled: true,
        },
      }}
    >
      <FavoritesPopperContent onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <FavoritesHeader>
          <FavoriteIcon>
            <FontAwesomeIcon icon={faStar} />
          </FavoriteIcon>
          <FavoriteHeaderTitle>Favourites</FavoriteHeaderTitle>
          <FavoriteCount>{sortedFavorites.length}</FavoriteCount>
        </FavoritesHeader>

        <FavoritesContent>
          {sortedFavorites.length === 0 ? (
            <EmptyState>
              <FontAwesomeIcon icon={faStarOutline} />
              <EmptyText>No favourites yet</EmptyText>
            </EmptyState>
          ) : (
            sortedFavorites.map((favorite) => {
              const moduleId = generateMenuItemId(favorite.module);
              return (
                <FavoriteItem key={moduleId} onClick={() => onModuleClick(favorite.module)}>
                  <ModuleIconWrapper>
                    <FontAwesomeIcon icon={getModuleIcon(favorite.module.title)} />
                  </ModuleIconWrapper>
                  <ModuleDetails>
                    <ModuleName>{favorite.module.title}</ModuleName>
                    <CourseInfo>
                      <ReactCountryFlag
                        countryCode={LANGUAGE_FLAGS[favorite.language] || 'XX'}
                        svg
                        style={{
                          width: '14px',
                          height: '10px',
                        }}
                      />
                      <Separator>•</Separator>
                      <CourseText>{favorite.courseName}</CourseText>
                    </CourseInfo>
                  </ModuleDetails>
                  <RemoveButton onClick={(e) => handleRemoveFavorite(e, moduleId)} title="Remove from favourites">
                    <FontAwesomeIcon icon={faTimes} />
                  </RemoveButton>
                </FavoriteItem>
              );
            })
          )}
        </FavoritesContent>
      </FavoritesPopperContent>
    </Popper>
  );
};

// Styled components specific to favorites dropdown
const FavoritesPopperContent = styled(Paper)`
  background: #242837 !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  min-width: 300px;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
  overflow: hidden;
`;

const FavoritesHeader = styled(DropdownHeader)`
  background: rgba(255, 183, 0, 0.05);
  border-bottom: 1px solid rgba(255, 183, 0, 0.2);
  display: flex;
  align-items: center;
  padding: 12px 16px;
`;

const FavoriteIcon = styled.div`
  margin-right: 10px;
  svg {
    color: #ffb700;
    font-size: 16px;
  }
`;

const FavoriteHeaderTitle = styled.div`
  font-size: 16px;
  color: #fff;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const FavoritesContent = styled.div`
  max-height: 400px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }
`;

const FavoriteItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.05);

    button {
      opacity: 1;
    }
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
`;

const ModuleDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 12px;
`;

const CourseInfo = styled.div`
  display: flex;
  align-items: center;
  margin-top: 3px;
`;

const CourseText = styled.span`
  font-size: 12px;
  color: #8b92a8;
`;

const Separator = styled.span`
  color: #4b5563;
  margin: 0 6px;
  font-size: 12px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  padding: 4px;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;
  border-radius: 4px;

  &:hover {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.1);
  }

  svg {
    font-size: 12px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #6b7280;

  svg {
    font-size: 24px;
    margin-bottom: 12px;
    opacity: 0.5;
  }
`;

const EmptyText = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const FavoriteCount = styled.span`
  font-size: 12px;
  color: #ffb700;
  background: rgba(255, 183, 0, 0.2);
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: auto;
  font-weight: 600;
`;
